// web/app-src/components/backup-manager.ts
import AppComponent from '../base/app-component.ts';

interface BackupFile {
  filename: string;
  kind: 'backup' | 'replaced-by';
  timestamp: string;
  size: number;
}

interface ListResponse {
  backups: BackupFile[];
  replaced: BackupFile[];
}

interface ViewItem extends BackupFile {
  label: string;
  sizeLabel: string;
}

export class BackupManager extends AppComponent {
  static override componentName = 'wc-backup-manager';

  protected override get templateId() { return 'backup-manager'; }

  private data: ListResponse = { backups: [], replaced: [] };
  private loaded = false;

  protected override buildRenderingData(): Record<string, unknown> {
    const base = super.buildRenderingData();
    return {
      ...base,
      loaded: this.loaded,
      backups: this.data.backups.map(this.toView),
      replaced: this.data.replaced.map(this.toView),
    };
  }

  protected override onConnected(): void {
    // Listener delegate sul componente: agganciati al root, sopravvivono ai re-render.
    // Bind UNA SOLA VOLTA qui per evitare accumulo (e quindi N toast / N confirm).
    this.delegate.on<MouseEvent>('click', '[data-action="create"]', (e) => {
      e.preventDefault();
      void this.handleCreate();
    });
    this.delegate.on<MouseEvent>('click', '[data-action="restore"]', (e) => {
      const btn = e.target instanceof Element ? e.target.closest<HTMLElement>('[data-filename]') : null;
      const filename = btn?.dataset.filename;
      if (filename) {
        e.preventDefault();
        void this.handleRestore(filename);
      }
    });

    // Carica dati e ri-renderizza una volta pronti.
    void this.reload().then(() => this.render());
  }

  private async reload(): Promise<void> {
    try {
      const res = await fetch('/api/backup/list');
      if (!res.ok) {
        this.data = { backups: [], replaced: [] };
      } else {
        this.data = await res.json() as ListResponse;
      }
    } catch {
      this.data = { backups: [], replaced: [] };
    }
    this.loaded = true;
  }

  private async handleCreate(): Promise<void> {
    try {
      const res = await fetch('/api/backup', { method: 'POST' });
      const body = await res.json() as { filename?: string; error?: string };
      if (!res.ok) {
        await Weext.alert(body.error ?? 'Errore durante il backup');
        return;
      }
      Weext.toast(`Backup creato: ${body.filename}`, { type: 'success' });
      await this.reload();
      this.render();
    } catch (e) {
      await Weext.alert(`Errore: ${(e as Error).message}`);
    }
  }

  private async handleRestore(filename: string): Promise<void> {
    const ok = await Weext.confirm(
      `Il database attivo verrà rinominato come "replaced-by-...db" e sostituito con "${filename}". Procedere?`
    );
    if (!ok) return;

    try {
      const res = await fetch('/api/backup/restore', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename }),
      });
      const body = await res.json() as { replacedAs?: string; error?: string };
      if (!res.ok) {
        await Weext.alert(body.error ?? 'Errore durante il ripristino');
        return;
      }
      Weext.toast(`Ripristino completato. Versione precedente: ${body.replacedAs}`, { type: 'success' });
      setTimeout(() => location.reload(), 1200);
    } catch (e) {
      await Weext.alert(`Errore: ${(e as Error).message}`);
    }
  }

  private toView = (b: BackupFile): ViewItem => ({
    ...b,
    label: this.formatTimestamp(b.timestamp),
    sizeLabel: this.formatSize(b.size),
  });

  private formatTimestamp(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }
}
